import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import RefreshIcon from '@material-ui/icons/Refresh';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { createFormField } from '@backstage/plugin-scaffolder-react/alpha';

export interface ServerItem {
  id: string;
  name: string;
  provider: string;
  environment: string;
  ip: string;
  runtimes: ('compose' | 'kubernetes')[];
  targets?: Record<string, string>;
}

export interface EnvironmentSetupValue {
  environment?: 'staging' | 'product' | 'all';
  server?: string;
  serverName?: string;
  serverIp?: string;
  runtime?: 'compose' | 'kubernetes';
  clusterUrl?: string;
  externalPort?: number;
  containerPort?: number;
}

export interface EnvironmentServerPickerProps {
  onChange: (value: EnvironmentSetupValue) => void;
  formData?: EnvironmentSetupValue;
  required?: boolean;
  rawErrors?: string[];
  idSchema?: {
    $id: string;
  };
  schema?: {
    title?: string;
    description?: string;
  };
}

export const EnvironmentServerPickerComponent = (
  props: EnvironmentServerPickerProps,
) => {
  const { onChange, formData = {}, rawErrors, schema } = props;
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  // Project Provisioning strictly limits environment to staging
  const [servers, setServers] = useState<ServerItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchServers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const baseUrl = await discoveryApi.getBaseUrl('template-provisioning');
      const res = await fetchApi.fetch(
        `${baseUrl}/servers?environment=staging`,
      );
      if (!res.ok) {
        throw new Error(`Failed to load servers: HTTP ${res.status}`);
      }
      const data = await res.json();
      const items: ServerItem[] = data.items || [];
      setServers(items);

      // Check if previously selected server is still valid under staging filter
      if (formData.server && !items.some(s => s.id === formData.server)) {
        onChange({
          ...formData,
          environment: 'staging',
          server: undefined,
          serverName: undefined,
          serverIp: undefined,
          runtime: undefined,
        });
      } else if (formData.environment !== 'staging') {
        onChange({
          ...formData,
          environment: 'staging',
        });
      }
    } catch (err: any) {
      setError(err.message || 'Error fetching server list from abc-api');
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, fetchApi, formData, onChange]);

  useEffect(() => {
    fetchServers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedServer = servers.find(s => s.id === formData.server);
  const allowedRuntimes = ['compose', 'kubernetes'];
  const matchingServers = servers.filter(
    server => !formData.runtime || server.runtimes.includes(formData.runtime),
  );

  const handleServerChange = (e: ChangeEvent<{ value: unknown }>) => {
    const nextServerId = e.target.value as string;
    const serverObj = servers.find(s => s.id === nextServerId);
    let nextRuntime = formData.runtime;

    if (serverObj) {
      if (!nextRuntime || !serverObj.runtimes.includes(nextRuntime)) {
        nextRuntime = serverObj.runtimes[0] || 'compose';
      }
    }

    onChange({
      ...formData,
      environment: 'staging',
      server: nextServerId,
      serverName: serverObj?.name,
      serverIp: serverObj?.ip,
      runtime: nextRuntime,
    });
  };

  const handleRuntimeChange = (e: ChangeEvent<{ value: unknown }>) => {
    onChange({
      ...formData,
      environment: 'staging',
      runtime: e.target.value as 'compose' | 'kubernetes',
      server: undefined,
      serverName: undefined,
      serverIp: undefined,
    });
  };

  const handleExternalPortChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value ? parseInt(e.target.value, 10) : undefined;
    onChange({
      ...formData,
      externalPort: val,
    });
  };

  const handleContainerPortChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value ? parseInt(e.target.value, 10) : undefined;
    onChange({
      ...formData,
      containerPort: val,
    });
  };

  const hasErrors = rawErrors && rawErrors.length > 0;

  return (
    <Box marginY={2}>
      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
        {schema?.title ?? 'Environment Setup'}
      </Typography>
      <Typography
        variant="body2"
        color="textSecondary"
        style={{ marginBottom: 16 }}
      >
        {schema?.description ??
          'Configure deployment environment, server, runtime platform, and host port reservation.'}
      </Typography>

      {error && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => fetchServers()}
            >
              Retry
            </Button>
          }
          style={{ marginBottom: 16 }}
        >
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* 1. Môi trường (Cố định Staging cho Project Provisioning) */}
        <Grid item xs={12} sm={6}>
          <TextField
            variant="outlined"
            fullWidth
            margin="dense"
            label="1. Môi Trường Triển Khai"
            value="Staging (Cố định)"
            InputProps={{ readOnly: true }}
            helperText="Project Provisioning Template cố định môi trường staging"
          />
        </Grid>

        {/* 3. Runtime Platform */}
        <Grid item xs={12} sm={6}>
          <FormControl
            variant="outlined"
            fullWidth
            margin="dense"
            error={Boolean(!formData.runtime && hasErrors)}
          >
            <InputLabel id="runtime-select-label">
              3. Runtime Platform
            </InputLabel>
            <Select
              labelId="runtime-select-label"
              id="runtime-select"
              value={formData.runtime || ''}
              onChange={handleRuntimeChange}
              label="3. Runtime Platform"
              disabled={loading}
            >
              {allowedRuntimes.includes('compose') && (
                <MenuItem value="compose">Docker Compose (compose)</MenuItem>
              )}
              {allowedRuntimes.includes('kubernetes') && (
                <MenuItem value="kubernetes">Kubernetes (k8s)</MenuItem>
              )}
            </Select>
            <FormHelperText>
              {selectedServer
                ? `Supported runtimes on ${
                    selectedServer.name
                  }: [${allowedRuntimes.join(', ')}]`
                : 'Select runtime first, then choose a compatible server'}
            </FormHelperText>
          </FormControl>
        </Grid>

        {/* 2. Server Picker */}
        <Grid item xs={12} sm={6}>
          <FormControl
            variant="outlined"
            fullWidth
            margin="dense"
            error={Boolean(!formData.server && hasErrors)}
          >
            <InputLabel id="server-select-label">2. Target Server</InputLabel>
            <Select
              labelId="server-select-label"
              id="server-select"
              value={formData.server || ''}
              onChange={handleServerChange}
              label="2. Target Server"
              disabled={
                loading || !formData.runtime || matchingServers.length === 0
              }
            >
              {matchingServers.map(s => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name} ({s.provider.toUpperCase()}) &bull; {s.environment}{' '}
                  &bull; {s.ip}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {loading
                ? 'Loading servers from abc-api...'
                : servers.length === 0
                ? 'No servers found for this environment'
                : 'Select host server'}
            </FormHelperText>
          </FormControl>
        </Grid>

        {formData.runtime === 'kubernetes' && (
          <Grid item xs={12}>
            <TextField
              label="Kubernetes API server"
              fullWidth
              required
              variant="outlined"
              margin="dense"
              value={formData.clusterUrl || ''}
              onChange={event =>
                onChange({ ...formData, clusterUrl: event.target.value })
              }
              helperText="API server origin for the selected cluster, e.g. https://cluster.example.com:6443"
            />
          </Grid>
        )}
        {/* 4. External Port */}
        <Grid item xs={12} sm={6}>
          <TextField
            id="external-port-input"
            label="4. External Host Port"
            type="number"
            variant="outlined"
            margin="dense"
            fullWidth
            required
            value={formData.externalPort ?? ''}
            onChange={handleExternalPortChange}
            helperText="External host port (1 - 65535). Reserved across the server IP."
            inputProps={{ min: 1, max: 65535 }}
            error={Boolean(
              formData.externalPort &&
                (formData.externalPort < 1 || formData.externalPort > 65535),
            )}
          />
        </Grid>

        {/* Container Port (Optional) */}
        <Grid item xs={12} sm={6}>
          <TextField
            id="container-port-input"
            label="Container Port (Optional)"
            type="number"
            variant="outlined"
            margin="dense"
            fullWidth
            value={formData.containerPort ?? 3000}
            onChange={handleContainerPortChange}
            helperText="Internal application container port (default: 3000). Not checked for uniqueness."
            inputProps={{ min: 1, max: 65535 }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export const environmentServerValidation = (value: any, validation: any) => {
  if (!value || typeof value !== 'object') {
    validation.addError('Environment configuration is required');
    return;
  }
  if (!value.server) {
    validation.addError('Please select a deployment server');
  }
  if (!value.runtime) {
    validation.addError('Please select a runtime platform');
  }
  if (value.runtime === 'kubernetes') {
    try {
      const url = new URL(value.clusterUrl || '');
      if (
        !['https:', 'http:'].includes(url.protocol) ||
        url.username ||
        url.password ||
        url.pathname !== '/' ||
        url.search ||
        url.hash
      )
        throw new Error();
    } catch {
      validation.addError('Provide the Kubernetes API server origin');
    }
  }
  if (!value.externalPort) {
    validation.addError('External port is required');
  } else {
    const p = Number(value.externalPort);
    if (!Number.isInteger(p) || p < 1 || p > 65535) {
      validation.addError(
        'External port must be an integer between 1 and 65535',
      );
    }
  }
};

export const EnvironmentServerPicker = createFormField({
  name: 'EnvironmentServerPicker',
  component: EnvironmentServerPickerComponent as any,
  validation: environmentServerValidation as any,
});
